import StateObserver from 'src/StateObserver';
import {
  setCompensation,
  setDisplayFrozen,
} from 'src/game/inventory/inventory.redux';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { PuzzleResults } from 'src/replicant/match2/match2.ruleset';
import { openMatch2RewardsPopup } from '../rewards/match2-rewards.utils';
import { getMatch2UnclaimedFrenzyMilestoneRewards } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import {
  createRewardCompensation,
  updateRewardCompensation,
} from '../match2.util';

/**
 * Open Match2 frenzy intro popup.
 */
export async function openMatch2FrenzyIntroPopup(args: {
  skipPlay?: boolean;
  setLastOpened?: boolean;
  origin: PopupDialogOrigin | undefined;
}): Promise<boolean> {
  const { skipPlay = false, setLastOpened = false, origin } = args;
  const success = await openPopupPromise('popupMatch2FrenzyIntro', {
    useClickOutside: true,
    skipPlay,
    origin,
  });
  if (setLastOpened) {
    await StateObserver.invoke.setMatch2FrenzyIntroTimestamp();
  }
  return success;
}

/**
 * Claim any pending Match2 frenzy rewards, and display them with the regular rewards popup.
 * @returns
 */
export async function match2FrenzyClaimPendingRewards() {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const rawRewards = getMatch2UnclaimedFrenzyMilestoneRewards(user, now);
  if (!rawRewards.length) return [];
  const rewards = processMatch2Rewards(rawRewards, now);
  await StateObserver.invoke.claimMatch2FrenzyRewards(now);
  return rewards;
}

/**
 * Run Match2 frenzy post game logic.
 */
export async function match2FrenzyPostGame({
  result,
}: {
  result: PuzzleResults['result'];
}) {
  const hasWon = result === 'win';
  if (!hasWon) return [];
  const inventory = StateObserver.getState().inventory;
  const displayFrozen = inventory.displayFrozen;
  const currentCompensation = inventory.compensation;
  StateObserver.dispatch(setDisplayFrozen(true));
  try {
    const rewards = await match2FrenzyClaimPendingRewards();
    const compensation = createRewardCompensation();
    compensation.bPoints = currentCompensation.bPoints;
    compensation.gems = currentCompensation.gems;
    compensation.energy = currentCompensation.energy;
    rewards.forEach((reward) => {
      updateRewardCompensation(compensation, reward, 1);
    });
    StateObserver.dispatch(setCompensation(compensation));
    return rewards;
  } finally {
    StateObserver.dispatch(setDisplayFrozen(displayFrozen));
  }
}

/**
 * Run Match2 frenzy launch sequence logic.
 */
export async function match2FrenzyLaunch() {
  const rewards = await match2FrenzyClaimPendingRewards();
  if (!rewards || !rewards.length) return;
  await openMatch2RewardsPopup({
    rewards,
    useSfx: true,
    origin: 'launch',
    noGrantAnimation: true,
  });
}
